var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import CollectionItemV2 from "./CollectionItemV2";
import ArticleItemV2 from "./ArticleItemV2";
import ArticleV2 from "./ArticleV2";
import { getReq } from "../../request";
import { KB_COLLECTION_CHILDREN_BY_ID, KB_SEARCH_URL_PATH_V2 } from "../../globals";
import CloseWidgetPanel from "../CloseWidgetPanel";
import { AppContext } from "../../appContext";
import { resizeFrame } from "../../Utils";
import { PromtWidth } from "../../App";
var CollectionDashboard = function (_a) {
    var items = _a.items, _b = _a.searchTxt, searchTxt = _b === void 0 ? '' : _b, setSearchTxt = _a.setSearchTxt;
    var parentContext = useContext(AppContext);
    var setPromtWidth = parentContext.setPromtWidth;
    var promtWidth = parentContext.promtWidth;
    // State to track the current view
    var _c = useState('main'), currentView = _c[0], setCurrentView = _c[1];
    // State to store the current items being displayed (could be main items, collection children, or a single article)
    var _d = useState(items), displayItems = _d[0], setDisplayItems = _d[1];
    // State to store the current collection being viewed
    var _e = useState(null), currentCollection = _e[0], setCurrentCollection = _e[1];
    // State to store the current article when in article view
    var _f = useState(null), currentArticle = _f[0], setCurrentArticle = _f[1];
    // State to track loading status
    var _g = useState(false), isLoading = _g[0], setIsLoading = _g[1];
    // State for search functionality
    var _h = useState(searchTxt), display = _h[0], setDisplay = _h[1];
    var _j = useState(false), loading = _j[0], setLoading = _j[1];
    // State for search results
    var _k = useState([]), searchResults = _k[0], setSearchResults = _k[1];
    var _l = useState(false), isSearching = _l[0], setIsSearching = _l[1];
    // State to store navigation history for back navigation
    var _m = useState([
        { parentId: null, collectionId: null, title: 'Help Center', description: '' }
    ]), navigationHistory = _m[0], setNavigationHistory = _m[1];
    // Check sessionStorage on component mount to restore previous collection view
    useEffect(function () {
        var savedCollectionId = sessionStorage.getItem('currentCollectionId');
        var savedArticleId = sessionStorage.getItem('currentArticleId');
        var savedNavigationHistory = sessionStorage.getItem('navigationHistory');
        // Restore navigation history if available
        var parsedHistory = [{ parentId: null, collectionId: null, title: 'Help Center', description: '' }];
        if (savedNavigationHistory) {
            try {
                var parsed = JSON.parse(savedNavigationHistory);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    parsedHistory = parsed;
                    setNavigationHistory(parsed);
                }
            }
            catch (e) {
                // console.log("Error parsing saved navigation history:", e);
            }
        }
        // Check if we have a saved article ID
        if (savedArticleId) {
            setCurrentArticle({ id: savedArticleId });
            setCurrentView('article');
            return; // Exit early as article view takes precedence
        }
        // Check if we have a saved collection ID
        if (savedCollectionId) {
            // Fetch the collection data using the saved ID
            setIsLoading(true);
            getReq("".concat(KB_COLLECTION_CHILDREN_BY_ID, "/").concat(savedCollectionId), {})
                .then(function (response) {
                if (response && response.data) {
                    // Ensure response.data is an array
                    var responseData = Array.isArray(response.data) ? response.data : [];
                    // Update display items with the fetched collections
                    setDisplayItems(responseData);
                    // Set view to collection
                    setCurrentView('collection');
                    // Find the collection in the navigation history to set as current collection
                    var collectionEntry = parsedHistory.find(function (item) { return item.collectionId === savedCollectionId; });
                    if (collectionEntry) {
                        setCurrentCollection({
                            id: savedCollectionId,
                            title: collectionEntry.title,
                            description: collectionEntry.description || '',
                            parentId: collectionEntry.parentId,
                            type: 'COLLECTION'
                        });
                    }
                }
                setIsLoading(false);
            })
                .catch(function (e) {
                // console.log("Error fetching saved collection:", e);
                setIsLoading(false);
            });
        }
    }, []); // Empty dependency array ensures this runs only once on mount
    // Update displayItems when items prop changes
    useEffect(function () {
        if (currentView === 'main') {
            // Ensure items is always an array
            var safeItems = Array.isArray(items) ? items : [];
            setDisplayItems(safeItems);
        }
    }, [items, currentView]);
    // Handle search text changes
    useEffect(function () {
        if (!setSearchTxt)
            return; // Skip if search functionality is not provided
        if (display.length < searchTxt.length || (display.length == searchTxt.length && display != searchTxt))
            setLoading(true);
        else if (display.length == searchTxt.length)
            return;
        var timeoutId = setTimeout(function () {
            setLoading(true);
            setDisplay(searchTxt);
            var wait = searchTxt
                ? getReq(KB_SEARCH_URL_PATH_V2, { q: searchTxt })
                : getReq(KB_COLLECTION_CHILDREN_BY_ID, {});
            wait.then(function (response) {
                var _a, _b, _c, _d;
                if (searchTxt) {
                    // Handle search results
                    var articleResults = [];
                    // Check if response.data is an array (direct search results)
                    if (Array.isArray(response.data)) {
                        articleResults = response.data.filter(function (item) {
                            return item.status === 'PUBLISHED';
                        });
                    }
                    // Check if response.data.data is an array (v2 API format)
                    else if (((_a = response.data) === null || _a === void 0 ? void 0 : _a.data) && Array.isArray(response.data.data)) {
                        articleResults = response.data.data.filter(function (item) {
                            return item.type === 'ARTICLE' || item.status === 'PUBLISHED';
                        });
                    }
                    setSearchResults(articleResults);
                    setIsSearching(true);
                }
                else {
                    // Handle regular display items (for v2 API)
                    if (((_b = response.data) === null || _b === void 0 ? void 0 : _b.version) && ((_c = response.data) === null || _c === void 0 ? void 0 : _c.version.toLowerCase()) === 'v2') {
                        setDisplayItems(((_d = response === null || response === void 0 ? void 0 : response.data) === null || _d === void 0 ? void 0 : _d.data) || []);
                    }
                    setSearchResults([]);
                    setIsSearching(false);
                }
                setLoading(false);
            }).catch(function (e) {
                // console.log(e);
                setSearchResults([]);
                setIsSearching(false);
                setLoading(false);
            });
        }, 1000);
        return function () { return clearTimeout(timeoutId); };
    }, [searchTxt]);
    // Fetch collections by parent ID
    var fetchCollectionsByParentId = function (parentId) {
        setIsLoading(true);
        // If parentId is null, we're fetching root collections
        var endpoint = parentId
            ? "".concat(KB_COLLECTION_CHILDREN_BY_ID, "/").concat(parentId)
            : KB_COLLECTION_CHILDREN_BY_ID;
        getReq(endpoint, {})
            .then(function (response) {
            if (response && response.data) {
                // Ensure response.data is an array
                var responseData = Array.isArray(response.data) ? response.data : [];
                // Update display items with the fetched collections
                setDisplayItems(responseData);
                // If we're going back to root, clear current collection and set view to main
                if (!parentId) {
                    setCurrentCollection(null);
                    setCurrentView('main');
                }
                else {
                    // We're viewing a collection's children, stay in collection view
                    setCurrentView('collection');
                }
            }
            else {
                // If no data in response, set empty array
                setDisplayItems([]);
            }
            setIsLoading(false);
        })
            .catch(function (e) {
            // console.log("Error fetching collections:", e);
            setIsLoading(false);
            // On error, go back to root collections
            setDisplayItems(Array.isArray(items) ? items : []);
            setCurrentCollection(null);
            setCurrentView('main');
        });
    };
    // Handle collection click
    var handleCollectionClick = function (collectionId, children) {
        // Find the clicked collection
        var clickedCollection = displayItems.find(function (item) { return item.id === collectionId; });
        if (clickedCollection) {
            // Set loading to false since we received the children data
            setIsLoading(false);
            // Set the current collection
            setCurrentCollection(clickedCollection);
            // Update display items with the children
            setDisplayItems(children);
            // Set view to collection
            setCurrentView('collection');
            // Create updated navigation history
            var updatedHistory = __spreadArray(__spreadArray([], navigationHistory, true), [
                {
                    parentId: clickedCollection.parentId || null,
                    collectionId: clickedCollection.id,
                    title: clickedCollection.title,
                    description: clickedCollection.description || ''
                }
            ], false);
            // Add to navigation history with description
            setNavigationHistory(updatedHistory);
            // Save to sessionStorage for persistence across page refreshes
            sessionStorage.setItem('currentCollectionId', collectionId);
            sessionStorage.setItem('navigationHistory', JSON.stringify(updatedHistory));
        }
    };
    // Handle collection click start (when user clicks but before API call completes)
    var handleCollectionClickStart = function (collectionId) {
        // Set loading state to true when collection is clicked
        setIsLoading(true);
    };
    // Handle article click
    var handleArticleClick = function (articleId) {
        // Find the clicked article to get its details
        var clickedArticle = displayItems.find(function (item) { return item.id === articleId; });
        // Set view to article
        setCurrentView('article');
        // Set the current article ID
        setCurrentArticle({ id: articleId });
        // Add to navigation history if we found the article in display items
        if (clickedArticle) {
            var updatedHistory = __spreadArray(__spreadArray([], navigationHistory, true), [
                {
                    parentId: currentCollection ? currentCollection.id : null,
                    collectionId: null,
                    title: clickedArticle.title,
                    description: clickedArticle.description || ''
                }
            ], false);
            setNavigationHistory(updatedHistory);
            // Save article ID and updated navigation history to sessionStorage
            sessionStorage.setItem('currentArticleId', articleId);
            sessionStorage.setItem('navigationHistory', JSON.stringify(updatedHistory));
            // Keep the collection ID in sessionStorage if we're viewing an article within a collection
            if (currentCollection && currentCollection.id) {
                sessionStorage.setItem('currentCollectionId', currentCollection.id);
            }
        }
    };
    // Handle search article selection
    var handleSearchArticleSelect = function (article) {
        // Set view to article
        setCurrentView('article');
        // Set the current article ID
        setCurrentArticle({ id: article.id });
        // Add to navigation history
        var updatedHistory = __spreadArray(__spreadArray([], navigationHistory, true), [
            {
                parentId: currentCollection ? currentCollection.id : null,
                collectionId: null,
                title: article.title,
                description: article.description || ''
            }
        ], false);
        setNavigationHistory(updatedHistory);
        // Save article ID and updated navigation history to sessionStorage
        sessionStorage.setItem('currentArticleId', article.id);
        sessionStorage.setItem('navigationHistory', JSON.stringify(updatedHistory));
        // Clear search text and results
        if (setSearchTxt) {
            setSearchTxt('');
        }
        setSearchResults([]);
        setIsSearching(false);
    };
    // Handle back navigation
    var handleBackNavigation = function () {
        // If we only have the root in history, we can't go back further
        if (navigationHistory.length <= 1) {
            return;
        }
        // Remove the current level from history
        var newHistory = __spreadArray([], navigationHistory, true);
        newHistory.pop();
        setNavigationHistory(newHistory);
        // Get the previous level
        var previousLevel = newHistory[newHistory.length - 1];
        if (currentView === 'article') {
            if (promtWidth === PromtWidth.Large) {
                setPromtWidth(PromtWidth.Small);
            }
            // Going back from article to collection or main view
            setCurrentView(previousLevel.collectionId ? 'collection' : 'main');
            setCurrentArticle(null);
            // Remove article ID from sessionStorage
            sessionStorage.removeItem('currentArticleId');
            // Update navigation history in sessionStorage
            sessionStorage.setItem('navigationHistory', JSON.stringify(newHistory));
        }
        else if (currentView === 'collection') {
            // If we're going back to the root level
            if (previousLevel.parentId === null && previousLevel.collectionId === null) {
                fetchCollectionsByParentId(null); // Fetch root collections
                // Clear sessionStorage when going back to main view
                sessionStorage.removeItem('currentCollectionId');
                sessionStorage.removeItem('navigationHistory');
            }
            else {
                // Use the parentId to fetch its children
                fetchCollectionsByParentId(previousLevel.collectionId || previousLevel.parentId);
                // Update sessionStorage with the new current collection and history
                if (previousLevel.collectionId) {
                    sessionStorage.setItem('currentCollectionId', previousLevel.collectionId);
                    sessionStorage.setItem('navigationHistory', JSON.stringify(newHistory));
                }
            }
        }
    };
    // Back button is now rendered in the header
    // Render the header description
    var renderHeader = function () {
        if (currentView === 'collection') {
            var currentLevel = navigationHistory[navigationHistory.length - 1];
            return currentLevel.description && _jsx(_Fragment, { children: _jsxs("header", __assign({ className: "collection-header" }, { children: [_jsx("h2", __assign({ className: "title" }, { children: currentLevel.title })), currentLevel.description && _jsx("p", __assign({ className: "desc" }, { children: currentLevel.description }))] })) });
        }
        else {
            return _jsx(_Fragment, {});
        }
    };
    // Render the article view
    var renderArticleView = function () {
        if (!currentArticle || !currentArticle.id) {
            return (_jsx("div", __assign({ className: "article-view" }, { children: _jsx("div", __assign({ className: "article-content" }, { children: _jsx("p", { children: "Loading article content..." }) })) })));
        }
        return (_jsx(ArticleV2, { articleId: currentArticle.id.toString(), onBackClick: handleBackNavigation }));
    };
    // Render the collection/items view
    var renderItemsView = function () {
        // If we're searching, show search results
        if (isSearching) {
            if (searchResults.length > 0) {
                return (_jsx(_Fragment, { children: _jsx("ul", __assign({ className: "help__collections-list" }, { children: searchResults.map(function (article) { return (_jsx("li", { children: _jsxs("div", __assign({ className: "help__collection-item", onClick: function () { return handleSearchArticleSelect(article); }, style: { cursor: 'pointer', padding: '16px', borderBottom: '1px solid #f0f0f0' } }, { children: [_jsx("div", __assign({ style: { fontWeight: '500', fontSize: '16px', marginBottom: '8px' } }, { children: article.title })), article.path && article.path.length > 0 && (_jsx("div", __assign({ style: { fontSize: '14px', color: '#666' } }, { children: article.path.map(function (p) { return p.name; }).join(' > ') })))] })) }, article.id)); }) })) }));
            }
            else {
                return (_jsx("div", __assign({ className: "help__collections-scrollbar" }, { children: _jsx("div", __assign({ className: "pad-content" }, { children: _jsxs("div", __assign({ style: { textAlign: 'center', padding: '40px 20px' } }, { children: [_jsx("h2", __assign({ className: "pad-content-title" }, { children: "No Results Found" })), _jsxs("p", __assign({ className: "pad-text" }, { children: ["No articles found for \"", searchTxt, "\". Try different keywords."] }))] })) })) })));
            }
        }
        // Regular collection/article view
        var itemsArray = Array.isArray(displayItems) ? displayItems : [];
        var collections = itemsArray.filter(function (item) { return item && item.type === 'COLLECTION'; });
        var articles = itemsArray.filter(function (item) { return item && item.type === 'ARTICLE'; });
        if (collections.length > 0 || articles.length > 0) {
            return (_jsx(_Fragment, { children: _jsxs("ul", __assign({ className: "help__collections-list" }, { children: [collections.map(function (item) { return (_jsx("li", { children: _jsx(CollectionItemV2, { collection: item, onCollectionClick: handleCollectionClick, onCollectionClickStart: handleCollectionClickStart }) }, item.id)); }), articles.map(function (item) { return (_jsx("li", { children: _jsx(ArticleItemV2, { article: item, onArticleClick: handleArticleClick }) }, item.id)); })] })) }));
        }
        else {
            return (_jsx("div", __assign({ className: "help__collections-scrollbar" }, { children: _jsx("div", __assign({ className: "pad-content" }, { children: isLoading ? (_jsx(_Fragment, { children: _jsxs("div", __assign({ className: "chat__form-loader1" }, { children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {})] })) })) : (_jsxs(_Fragment, { children: [_jsx("img", { className: "pad-no-content-img", src: "https://d2p078bqz5urf7.cloudfront.net/cloud/assets/livechat/no-articles-yet.svg", alt: "No articles" }), _jsx("h2", __assign({ className: "pad-content-title" }, { children: "No Articles" })), _jsx("p", __assign({ className: "pad-text" }, { children: "Access to the articles is currently unavailable." }))] })) })) })));
        }
    };
    // Search function
    var search = function (value) {
        if (setSearchTxt) {
            setSearchTxt(value);
        }
        // Clear search results when search is cleared
        if (!value) {
            setSearchResults([]);
            setIsSearching(false);
        }
    };
    return (_jsx("div", __assign({ className: "overflow___hiiden chat__help-collection-wrapper" }, { children: _jsx("div", __assign({ className: "chat__help chat__help-article ".concat(displayItems.length === 0 ? "show-flex" : "") }, { children: _jsxs("div", __assign({ className: "chat__conversation chat__create-ticket" }, { children: [_jsxs("div", __assign({ className: "chat__header  ".concat(currentView === 'main' ? "chat__help-header" : "", " ").concat(currentView === 'article' ? "chat__help__article-header" : "") }, { children: [_jsxs("div", __assign({ className: "ticket__header-action" }, { children: [currentView !== 'main' && (_jsx("div", __assign({ "data-trigger": "all", className: "chat__header-back chat__help-action", onClick: handleBackNavigation }, { children: _jsx("svg", __assign({ xmlns: "http://www.w3.org/2000/svg", width: "24", height: "24", fill: "none", viewBox: "0 0 24 24", color: "currentColor" }, { children: _jsx("path", { stroke: "#fff", "stroke-linecap": "round", "stroke-linejoin": "round", "stroke-width": "1.7", d: "m14 18-6-6 6-6" }) })) }))), currentView === 'main' && _jsx("div", { className: "chat__help-action" }), _jsx("div", __assign({ className: "chat__help-user" }, { children: _jsx("h3", __assign({ className: "chat__help-user-name" }, { children: "Help " })) })), _jsx("div", __assign({ className: "chat__help-end" }, { children: _jsx(CloseWidgetPanel, {}) }))] })), currentView === 'main' && (_jsxs("div", __assign({ className: "icon-input" }, { children: [_jsx("input", { className: "icon-input__text-field", type: "text", placeholder: "Search for help", value: searchTxt, onChange: function (e) { return search(e.target.value); }, disabled: !setSearchTxt }), _jsx("span", __assign({ className: "icon-input__icon", onClick: function () { return searchTxt && !loading && search(""); } }, { children: loading ? (_jsxs("svg", __assign({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 200 200" }, { children: [_jsx("rect", { fill: "#FFF", width: "100%", height: "100%" }), _jsxs("radialGradient", __assign({ id: "a12", cx: ".66", fx: ".66", cy: ".3125", fy: ".3125", gradientTransform: "scale(1.5)" }, { children: [_jsx("stop", { offset: "0", "stop-color": "#6C757D" }), _jsx("stop", { offset: ".3", "stop-color": "#6C757D", "stop-opacity": ".9" }), _jsx("stop", { offset: ".6", "stop-color": "#6C757D", "stop-opacity": ".6" }), _jsx("stop", { offset: ".8", "stop-color": "#6C757D", "stop-opacity": ".3" }), _jsx("stop", { offset: "1", "stop-color": "#6C757D", "stop-opacity": "0" })] })), _jsx("circle", __assign({ "transform-origin": "center", fill: "none", stroke: "url(#a12)", "stroke-width": "15", "stroke-linecap": "round", "stroke-dasharray": "200 1000", "stroke-dashoffset": "0", cx: "100", cy: "100", r: "70" }, { children: _jsx("animateTransform", { type: "rotate", attributeName: "transform", calcMode: "spline", dur: "2", values: "360;0", keyTimes: "0;1", keySplines: "0 0 1 1", repeatCount: "indefinite" }) })), _jsx("circle", { "transform-origin": "center", fill: "none", opacity: ".2", stroke: "#6C757D", "stroke-width": "15", "stroke-linecap": "round", cx: "100", cy: "100", r: "70" })] }))) : !searchTxt ? (_jsxs("svg", __assign({ width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", className: "search-icon", xmlns: "http://www.w3.org/2000/svg" }, { children: [_jsx("circle", { cx: "7.5", cy: "7.5", r: "4.625", stroke: "currentColor", "stroke-width": "1.75" }), _jsx("path", { d: "M13.3813 14.6187C13.723 14.9604 14.277 14.9604 14.6187 14.6187C14.9604 14.277 14.9604 13.723 14.6187 13.3813L13.3813 14.6187ZM10.3813 11.6187L13.3813 14.6187L14.6187 13.3813L11.6187 10.3813L10.3813 11.6187Z", fill: "currentColor" })] }))) : (_jsx("svg", __assign({ xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", fill: "currentColor", className: "bi bi-x-lg", viewBox: "0 0 16 16" }, { children: _jsx("path", { d: "M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" }) }))) }))] }))), currentView === 'article' && _jsxs("div", __assign({ "data-trigger": "all", className: "chat__header-toggle-expansion" }, { children: [_jsx("div", __assign({ className: "chat__header-icon chat__header-expand-icon", onClick: function () {
                                            resizeFrame(promtWidth && promtWidth == PromtWidth.Large
                                                ? "WINDOW_OPENED"
                                                : "WINDOW_OPENED_LARGE");
                                            setPromtWidth(promtWidth == PromtWidth.Large
                                                ? PromtWidth.Small
                                                : PromtWidth.Large);
                                        } }, { children: promtWidth == PromtWidth.Large ? (_jsxs("svg", __assign({ width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: "expanded-svg" }, { children: [_jsx("path", { d: "M1 1.00073L6 6.00073", stroke: "#ffffff", "stroke-width": "1.7" }), _jsx("path", { d: "M1.5 6.50073L6.5 6.50073L6.5 1.50073", stroke: "#ffffff", "stroke-width": "1.75" }), _jsx("path", { d: "M10 10.0017L15 15.0017", stroke: "#ffffff", "stroke-width": "1.75" }), _jsx("path", { d: "M9.5 14.5017L9.5 9.50171L14.5 9.50171", stroke: "#ffffff", "stroke-width": "1.75" })] }))) : (_jsxs("svg", __assign({ width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: "expand-svg" }, { children: [_jsx("path", { d: "M1.99902 2.00073L6.99903 7.00073", stroke: "#fff", "stroke-width": "1.7" }), _jsx("path", { d: "M6.49902 1.50073L1.49902 1.50073L1.49902 6.50073", stroke: "#fff", "stroke-width": "1.75" }), _jsx("path", { d: "M8.99902 9.00074L13.999 14.0007", stroke: "#fff", "stroke-width": "1.75" }), _jsx("path", { d: "M14.499 9.50073L14.499 14.5007L9.49902 14.5007", stroke: "#fff", "stroke-width": "1.75" })] }))) })), _jsx(CloseWidgetPanel, {})] }))] })), _jsxs("div", __assign({ className: "help__collections" }, { children: [_jsx("div", __assign({ className: "help__navigation-header help__description-container" }, { children: renderHeader() })), isLoading ? (_jsx("div", __assign({ className: "help__collections-scrollbar" }, { children: _jsx("div", __assign({ className: "pad-content" }, { children: _jsxs("div", __assign({ className: "chat__form-loader1" }, { children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {})] })) })) }))) : (_jsx(_Fragment, { children: currentView === 'article'
                                    ? renderArticleView()
                                    : renderItemsView() }))] }))] })) })) })));
};
export default CollectionDashboard;
